// src/pages/admin/adminpanel/SettingsPanel.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../utils/api';  

const Section = ({ icon, title, children }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10 overflow-hidden">
    <div className="px-6 py-4 border-b border-surface-container-high flex items-center gap-3">
      <span className={`material-symbols-outlined text-primary text-[22px]`}>{icon}</span>
      <h3 className="font-bold text-base">{title}</h3>
    </div>
    <div className="p-6 space-y-5">{children}</div>
  </div>
);

const Toggle = ({ label, sub, value, onChange }) => (
  <div className="flex items-center justify-between gap-4">
    <div>
      <p className="text-sm font-semibold text-on-surface">{label}</p>
      {sub && <p className="text-xs text-secondary mt-0.5">{sub}</p>}
    </div>
    <button
      onClick={() => onChange(!value)}
      className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0
                  ${value ? 'bg-primary' : 'bg-outline-variant/40'}`}>
      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow
                        transition-all ${value ? 'left-5' : 'left-0.5'}`} />
    </button>
  </div>
);

const Field = ({ label, type = 'text', value, onChange, placeholder }) => (
  <div>
    <label className="text-xs font-black uppercase tracking-widest text-secondary block mb-1.5">
      {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-4 py-2.5 rounded-xl border border-outline-variant/30
                 bg-surface-container-low text-sm text-on-surface
                 focus:outline-none focus:ring-2 focus:ring-primary/20
                 placeholder:text-outline-variant transition-all"
    />
  </div>
);

  const SecretRow = ({ secret, onSaved, showToast }) => {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState('');
    const [saving, setSaving] = useState(false);
    const [revealed, setRevealed] = useState(null);
    const [revealing, setRevealing] = useState(false);

    const toggleReveal = async () => {
      if (revealed !== null) { setRevealed(null); return; }
      if (!secret.masked) return;
      setRevealing(true);
      try {
        const { data } = await api.get(`/api/admin/secrets/${secret.key}/reveal`);
        setRevealed(data.value || '');
      } catch (err) {
        showToast(err.response?.data?.message || 'Failed to reveal', 'error');
      } finally { setRevealing(false); }
    };

    const save = async () => {
      if (!value.trim()) { showToast('Value is required', 'error'); return; }
      setSaving(true);
      try {
        const { data } = await api.put(`/api/admin/secrets/${secret.key}`, { value: value.trim() });
        if (data.renderSynced) {
          showToast(`${secret.label} updated (synced to Render)`);
        } else {
          showToast(`${secret.label} updated, but Render sync failed: ${data.renderError || 'unknown error'}`, 'error');
        }
        setEditing(false);
        setValue('');
        onSaved();
      } catch (err) {
        showToast(err.response?.data?.message || 'Failed to save', 'error');
      } finally { setSaving(false); }
    };

    return (
      <div className="rounded-xl border border-outline-variant/20 p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-on-surface">{secret.label}</p>
            <div className="flex items-center gap-2 mt-1">
              <code className="text-xs text-secondary bg-surface-container-low px-1.5 py-0.5 rounded break-all">
                {revealed !== null ? revealed : (secret.masked || 'Not configured')}
              </code>
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                secret.source === 'db'    ? 'bg-emerald-100 text-emerald-700' :
                secret.source === 'env'   ? 'bg-blue-100 text-blue-700' :
                                             'bg-red-100 text-red-600'
              }`}>
                {secret.source === 'db' ? 'Admin-set' : secret.source === 'env' ? 'Env default' : 'Unset'}
              </span>
            </div>
          </div>
          {!editing && (
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {secret.masked && (
                <button onClick={toggleReveal} disabled={revealing}
                  className="w-8 h-8 rounded-full flex items-center justify-center bg-surface-container
                             text-secondary hover:bg-surface-container-high transition-all disabled:opacity-50">
                  {revealing
                    ? <span className="w-3.5 h-3.5 border-2 border-secondary border-t-transparent rounded-full animate-spin" />
                    : <span className="material-symbols-outlined text-[16px]">
                        {revealed !== null ? 'visibility_off' : 'visibility'}
                      </span>
                  }
                </button>
              )}
              <button onClick={() => { setEditing(true); setRevealed(null); }}
                className="px-3 py-1.5 rounded-full text-xs font-bold bg-surface-container
                           text-secondary hover:bg-surface-container-high transition-all">
                Update
              </button>
            </div>
          )}
        </div>

        {editing && (
          <div className="mt-3 space-y-2">
            {secret.warning && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <span className="material-symbols-outlined text-amber-600 text-[16px] mt-0.5">warning</span>
                <p className="text-xs text-amber-700 font-medium">{secret.warning}</p>
              </div>
            )}
            <input
              type="text"
              value={value}
              onChange={e => setValue(e.target.value)}
              placeholder={`New value for ${secret.key}`}
              autoFocus
              className="w-full px-4 py-2.5 rounded-xl border border-outline-variant/30
                         bg-surface-container-low text-sm text-on-surface
                         focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => { setEditing(false); setValue(''); }}
                className="px-4 py-1.5 rounded-full text-xs font-bold text-secondary hover:bg-surface-container transition-all">
                Cancel
              </button>
              <button onClick={save} disabled={saving}
                className="px-4 py-1.5 rounded-full text-xs font-bold bg-primary text-white
                           hover:opacity-90 disabled:opacity-50 transition-all">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const PwdField = ({ label, value, onChange, placeholder }) => {
    const [show, setShow] = useState(false);
    return (
      <div>
        <label className="text-xs font-black uppercase tracking-widest text-secondary block mb-1.5">
          {label}
        </label>
        <div className="relative">
          <input
            type={show ? 'text' : 'password'}
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full px-4 py-2.5 pr-10 rounded-xl border border-outline-variant/30
                      bg-surface-container-low text-sm text-on-surface
                      focus:outline-none focus:ring-2 focus:ring-primary/20
                      placeholder:text-outline-variant transition-all"
          />
          <button type="button" onClick={() => setShow(s => !s)}
            className="absolute inset-y-0 right-3 flex items-center text-outline
                      hover:text-on-surface transition-colors" tabIndex={-1}>
            <span className="material-symbols-outlined text-[18px]">
              {show ? 'visibility_off' : 'visibility'}
            </span>
          </button>
        </div>
      </div>
    );
  };


export default function SettingsPanel() {
  // ── Platform settings ──────────────────────────────────────────────────
  const [siteName,        setSiteName]        = useState('Elite Goats');
  const [supportEmail,    setSupportEmail]    = useState('support@elitegoats.com');
  const [contactPhone,    setContactPhone]    = useState('');
  const [currency,        setCurrency]        = useState('INR');
  const [timezone,        setTimezone]        = useState('Asia/Kolkata');

  // ── Listing settings ──────────────────────────────────────────────────
  const [autoApprove,     setAutoApprove]     = useState(false);
  const [maxImages,       setMaxImages]       = useState('5');
  const [requireLocation, setRequireLocation] = useState(true);

  // ── User settings ─────────────────────────────────────────────────────
  const [allowRegistration, setAllowRegistration] = useState(true);
  const [emailVerification, setEmailVerification] = useState(false);
  const [allowButchers,     setAllowButchers]     = useState(true);

  // ── Notification settings ─────────────────────────────────────────────
  const [emailOnNewListing,  setEmailOnNewListing]  = useState(true);
  const [emailOnNewUser,     setEmailOnNewUser]      = useState(false);
  const [emailOnNewInquiry,  setEmailOnNewInquiry]   = useState(true);
  const [emailOnSale,        setEmailOnSale]         = useState(true);

  // ── Maintenance ───────────────────────────────────────────────────────
  const [maintenanceMode,   setMaintenanceMode]   = useState(false);
  const [maintenanceMsg,    setMaintenanceMsg]     = useState('We\'ll be back shortly.');

  // ── Toast ─────────────────────────────────────────────────────────────
  const [toast, setToast] = useState(null);
  const { user, updateUser } = useAuth();
  const [imgUploading, setImgUploading] = useState(false);
  const [broadcast, setBroadcast] = useState({ title: '', message: '', role: 'all' });
  const [broadcasting, setBroadcasting] = useState(false);
  const [pwdForm,   setPwdForm]   = useState({ current: '', newPwd: '', confirm: '' });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [platformFee, setPlatformFee] = useState(0);
  const [feeType, setFeeType] = useState('free');
  const [smsProvider, setSmsProvider] = useState('msg91');
  const [secrets, setSecrets] = useState([]);
  const [secretsLoading, setSecretsLoading] = useState(true);
  const [openCategory, setOpenCategory] = useState(null);

  useEffect(() => {
    api.get('/api/public/settings').then(({ data }) => {
      if (data.fee_type) setFeeType(data.fee_type);
      if (data.platform_fee) setPlatformFee(Number(data.platform_fee));
      if (data.sms_provider) setSmsProvider(data.sms_provider);
    }).catch(() => {});
  }, []);

  const loadSecrets = () => {
    setSecretsLoading(true);
    api.get('/api/admin/secrets')
      .then(({ data }) => setSecrets(data))
      .catch(() => {})
      .finally(() => setSecretsLoading(false));
  };

  useEffect(() => { loadSecrets(); }, []);

  const handlePasswordChange = async () => {
    if (!pwdForm.current || !pwdForm.newPwd || !pwdForm.confirm) { showToast('All fields required', 'error'); return; }
    if (pwdForm.newPwd !== pwdForm.confirm) { showToast('Passwords do not match', 'error'); return; }
    if (pwdForm.newPwd.length < 6) { showToast('Min 6 characters required', 'error'); return; }
    setPwdSaving(true);
    try {
      await api.put('/api/admin/change-password', {
        current_password: pwdForm.current,
        new_password:     pwdForm.newPwd,
      });
      showToast('Password changed successfully!');
      setPwdForm({ current: '', newPwd: '', confirm: '' });
    } catch (err) {
      showToast(err.response?.data?.error || 'Could not change password.', 'error');
    } finally { setPwdSaving(false); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImgUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const { data } = await api.put('/api/admin/profile-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateUser({ profile_image: data.profile_image });
      showToast('Profile photo updated!');
    } catch {
      showToast('Image upload failed.', 'error');
    } finally { setImgUploading(false); }
  };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSave = (section) => {
    // Wire to your API here: api.post('/api/admin/settings', { ... })
    showToast(`${section} settings saved successfully`);
  };

  const handleBroadcast = async () => {
    if (!broadcast.title || !broadcast.message) { showToast('Title and message required', 'error'); return; }
    setBroadcasting(true);
    try {
      const { data } = await api.post('/api/admin/broadcast', broadcast);
      showToast(data.message);
      setBroadcast({ title: '', message: '', role: 'all' });
    } catch (err) {
      showToast(err.response?.data?.message || 'Broadcast failed', 'error');
    } finally { setBroadcasting(false); }
  };

  // ─────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 max-w-3xl">

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5
                         rounded-2xl shadow-2xl border text-white animate-fade-in
                         ${toast.type === 'success'
                           ? 'bg-emerald-800 border-emerald-600'
                           : 'bg-red-800 border-red-600'}`}>
          <span className="material-symbols-outlined text-[20px]">
            {toast.type === 'success' ? 'check_circle' : 'error'}
          </span>
          <p className="text-xs font-bold">{toast.msg}</p>
        </div>
      )}

      {/* Page title */}
      <div>
        <h2 className="text-xl font-black text-on-surface">Platform Settings</h2>
        <p className="text-sm text-secondary mt-0.5">
          Manage site configuration, user controls, and notifications
        </p>
      </div>

      {/* ── Admin Avatar ─────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10 p-6">
        <h3 className="font-bold text-base mb-4">Admin Profile Photo</h3>
        <div className="flex items-center gap-5">
          <div
            className="relative group cursor-pointer flex-shrink-0"
            onClick={() => document.getElementById('admin-avatar-input').click()}
          >
            {user?.profile_image ? (
              <img src={user.profile_image} alt="avatar"
                  className="w-16 h-16 rounded-full object-cover border-4 border-primary/20" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-primary flex items-center
                              justify-center text-white font-black text-2xl border-4 border-primary/20">
                {(user?.name || 'A')[0].toUpperCase()}
              </div>
            )}
            <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100
                            transition-opacity flex items-center justify-center">
              {imgUploading
                ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <span className="material-symbols-outlined text-white text-[18px]">photo_camera</span>
              }
            </div>
            <input id="admin-avatar-input" type="file" accept="image/*"
                  className="hidden" onChange={handleImageUpload} />
          </div>
          <div>
            <p className="font-bold text-on-surface">{user?.name}</p>
            <p className="text-xs text-secondary">{user?.email}</p>
            <p className="text-xs text-outline mt-1">Click photo to change</p>
          </div>
        </div>
      </div>

      {/* ── Change Password ── */}   
      <Section icon="lock" title="Change Password">
        <PwdField label="Current Password" value={pwdForm.current}
          onChange={v => setPwdForm(p => ({ ...p, current: v }))}
          placeholder="Enter current password" />
        <PwdField label="New Password" value={pwdForm.newPwd}
          onChange={v => setPwdForm(p => ({ ...p, newPwd: v }))}
          placeholder="At least 6 characters" />
        <PwdField label="Confirm New Password" value={pwdForm.confirm}
          onChange={v => setPwdForm(p => ({ ...p, confirm: v }))}
          placeholder="Re-enter new password" />
        <div className="flex justify-end pt-2">
          <button onClick={handlePasswordChange} disabled={pwdSaving}
            className="px-6 py-2.5 rounded-full bg-primary text-white text-sm
                       font-bold hover:opacity-90 disabled:opacity-50 transition-all">
            {pwdSaving ? 'Updating…' : 'Update Password'}
          </button>
        </div>
      </Section>      

      {/* ── General ─────────────────────────────────────────────────── */}
      <Section icon="tune" title="General">
        <Field
          label="Site Name"
          value={siteName}
          onChange={setSiteName}
          placeholder="Elite Goats"
        />
        <Field
          label="Support Email"
          type="email"
          value={supportEmail}
          onChange={setSupportEmail}
          placeholder="support@example.com"
        />
        <Field
          label="Contact Phone"
          type="tel"
          value={contactPhone}
          onChange={setContactPhone}
          placeholder="+91 98765 43210"
        />

        <div className="grid grid-cols-2 gap-4">
          {/* Currency */}
          <div>
            <label className="text-xs font-black uppercase tracking-widest
                               text-secondary block mb-1.5">
              Currency
            </label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-outline-variant/30
                         bg-surface-container-low text-sm text-on-surface
                         focus:outline-none focus:ring-2 focus:ring-primary/20">
              <option value="INR">₹ INR — Indian Rupee</option>
              <option value="USD">$ USD — US Dollar</option>
              <option value="PKR">₨ PKR — Pakistani Rupee</option>
              <option value="AED">د.إ AED — UAE Dirham</option>
            </select>
          </div>

          {/* Timezone */}
          <div>
            <label className="text-xs font-black uppercase tracking-widest
                               text-secondary block mb-1.5">
              Timezone
            </label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-outline-variant/30
                         bg-surface-container-low text-sm text-on-surface
                         focus:outline-none focus:ring-2 focus:ring-primary/20">
              <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
              <option value="Asia/Karachi">Asia/Karachi (PKT)</option>
              <option value="Asia/Dubai">Asia/Dubai (GST)</option>
              <option value="UTC">UTC</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <button onClick={() => handleSave('General')}
            className="px-6 py-2.5 rounded-full bg-primary text-white text-sm
                       font-bold hover:opacity-90 transition-all shadow-sm">
            Save General Settings
          </button>
        </div>
      </Section>

      {/* ── Listings ─────────────────────────────────────────────────── */}
      <Section icon="pets" title="Listings">
        <Toggle
          label="Auto-approve new listings"
          sub="Listings go live immediately without admin review"
          value={autoApprove}
          onChange={setAutoApprove}
        />
        <Toggle
          label="Require location on listings"
          sub="Sellers must provide city and state"
          value={requireLocation}
          onChange={setRequireLocation}
        />
        <Field
          label="Max images per listing"
          type="number"
          value={maxImages}
          onChange={setMaxImages}
          placeholder="5"
        />

        {autoApprove && (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200
                          rounded-xl px-4 py-3">
            <span className="material-symbols-outlined text-amber-600 text-[18px] mt-0.5">
              warning
            </span>
            <p className="text-xs text-amber-700 font-medium">
              Auto-approve is ON. All new listings will go live without moderation.
              Make sure you have spam filters in place.
            </p>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <button onClick={() => handleSave('Listing')}
            className="px-6 py-2.5 rounded-full bg-primary text-white text-sm
                       font-bold hover:opacity-90 transition-all shadow-sm">
            Save Listing Settings
          </button>
        </div>
      </Section>

      {/* ── Users ────────────────────────────────────────────────────── */}
      <Section icon="manage_accounts" title="Users">
        <Toggle
          label="Allow new registrations"
          sub="Users can sign up for a new account"
          value={allowRegistration}
          onChange={setAllowRegistration}
        />
        <Toggle
          label="Require email verification"
          sub="New users must verify their email before logging in"
          value={emailVerification}
          onChange={setEmailVerification}
        />
        <Toggle
          label="Allow butcher accounts"
          sub="Butchers can register and browse listings"
          value={allowButchers}
          onChange={setAllowButchers}
        />

        {!allowRegistration && (
          <div className="flex items-start gap-3 bg-red-50 border border-red-200
                          rounded-xl px-4 py-3">
            <span className="material-symbols-outlined text-red-500 text-[18px] mt-0.5">
              block
            </span>
            <p className="text-xs text-red-600 font-medium">
              Registrations are currently disabled. New users cannot sign up.
            </p>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <button onClick={() => handleSave('User')}
            className="px-6 py-2.5 rounded-full bg-primary text-white text-sm
                       font-bold hover:opacity-90 transition-all shadow-sm">
            Save User Settings
          </button>
        </div>
      </Section>

      {/* ── Notifications ────────────────────────────────────────────── */}
      <Section icon="notifications" title="Email Notifications">
        <Toggle
          label="New listing submitted"
          sub="Email admin when a farmer submits a new listing"
          value={emailOnNewListing}
          onChange={setEmailOnNewListing}
        />
        <Toggle
          label="New user registered"
          sub="Email admin when a new user signs up"
          value={emailOnNewUser}
          onChange={setEmailOnNewUser}
        />
        <Toggle
          label="New inquiry received"
          sub="Email admin when a buyer sends an inquiry"
          value={emailOnNewInquiry}
          onChange={setEmailOnNewInquiry}
        />
        <Toggle
          label="Sale completed"
          sub="Email admin when an order is marked as completed"
          value={emailOnSale}
          onChange={setEmailOnSale}
        />

        <div className="flex justify-end pt-2">
          <button onClick={() => handleSave('Notification')}
            className="px-6 py-2.5 rounded-full bg-primary text-white text-sm
                       font-bold hover:opacity-90 transition-all shadow-sm">
            Save Notification Settings
          </button>
        </div>
      </Section>

      {/* ── Maintenance ──────────────────────────────────────────────── */}
      <Section icon="construction" title="Maintenance">
        <Toggle
          label="Maintenance Mode"
          sub="Site shows a maintenance page to all non-admin visitors"
          value={maintenanceMode}
          onChange={setMaintenanceMode}
        />

        {maintenanceMode && (
          <>
            <Field
              label="Maintenance Message"
              value={maintenanceMsg}
              onChange={setMaintenanceMsg}
              placeholder="We'll be back shortly."
            />
            <div className="flex items-start gap-3 bg-red-50 border border-red-200
                            rounded-xl px-4 py-3">
              <span className="material-symbols-outlined text-red-500 text-[18px] mt-0.5">
                warning
              </span>
              <p className="text-xs text-red-600 font-medium">
                Maintenance mode is <strong>ON</strong>. The site is currently
                inaccessible to regular users.
              </p>
            </div>
          </>
        )}

        <div className="flex justify-end pt-2">
          <button
            onClick={() => handleSave('Maintenance')}
            className={`px-6 py-2.5 rounded-full text-sm font-bold
                        transition-all shadow-sm
                        ${maintenanceMode
                          ? 'bg-red-600 hover:bg-red-700 text-white'
                          : 'bg-primary text-white hover:opacity-90'}`}>
            {maintenanceMode ? '⚠ Save & Keep Maintenance ON' : 'Save Maintenance Settings'}
          </button>
        </div>
      </Section>

      <Section icon="campaign" title="Broadcast Notification">
        <div>
          <label className="text-xs font-black uppercase tracking-widest text-secondary block mb-1.5">
            Send To
          </label>
          <select value={broadcast.role}
            onChange={e => setBroadcast(p => ({ ...p, role: e.target.value }))}
            className="w-full px-4 py-2.5 rounded-xl border border-outline-variant/30
                      bg-surface-container-low text-sm focus:outline-none focus:ring-2 focus:ring-primary/20">
            <option value="all">All Users</option>
            <option value="farmer">Farmers Only</option>
            <option value="butcher">Butchers Only</option>
            <option value="customer">Customers Only</option>
          </select>
        </div>
        <Field label="Title" value={broadcast.title}
          onChange={v => setBroadcast(p => ({ ...p, title: v }))}
          placeholder="e.g. Platform Maintenance Tonight" />
        <div>
          <label className="text-xs font-black uppercase tracking-widest text-secondary block mb-1.5">
            Message
          </label>
          <textarea rows={3} value={broadcast.message}
            onChange={e => setBroadcast(p => ({ ...p, message: e.target.value }))}
            placeholder="Write your announcement..."
            className="w-full px-4 py-2.5 rounded-xl border border-outline-variant/30
                      bg-surface-container-low text-sm focus:outline-none focus:ring-2
                      focus:ring-primary/20 resize-none" />
        </div>
        <div className="flex justify-end pt-2">
          <button onClick={handleBroadcast} disabled={broadcasting}
            className="px-6 py-2.5 rounded-full bg-primary text-white text-sm
                      font-bold hover:opacity-90 disabled:opacity-50 transition-all flex items-center gap-2">
            <span className="material-symbols-outlined text-[18px]">campaign</span>
            {broadcasting ? 'Sending…' : 'Send Broadcast'}
          </button>
        </div>
      </Section>

      <Section icon="payments" title="Platform Fee">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-black uppercase tracking-widest text-secondary block mb-1.5">
              Fee Type
            </label>
            <select
              value={feeType}
              onChange={e => setFeeType(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-outline-variant/30 bg-surface-container-low text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="free">Free</option>
              <option value="fixed">Fixed (₹)</option>
              <option value="percent">Percentage (%)</option>
            </select>
          </div>
          {feeType !== 'free' && (
            <Field
              label={feeType === 'percent' ? 'Percentage (%)' : 'Amount (₹)'}
              type="number"
              value={platformFee}
              onChange={v => setPlatformFee(v)}
              placeholder="0"
            />
          )}
        </div>
        <div className="flex justify-end pt-2">
          <button
            onClick={() => {
              api.post('/api/admin/settings', {
                platform_fee: feeType === 'free' ? 0 : platformFee,
                fee_type: feeType,
              });
              showToast('Platform fee saved');
            }}
            className="px-6 py-2.5 rounded-full bg-primary text-white text-sm font-bold hover:opacity-90 transition-all"
          >
            Save Fee Settings
          </button>
        </div>
      </Section>

      <Section icon="sms" title="SMS Provider">
        <div>
          <label className="text-xs font-black uppercase tracking-widest text-secondary block mb-1.5">
            Active Provider
          </label>
          <select value={smsProvider} onChange={e => setSmsProvider(e.target.value)}
            className="w-full px-4 py-2.5 rounded-xl border border-outline-variant/30
                      bg-surface-container-low text-sm focus:outline-none focus:ring-2 focus:ring-primary/20">
            <option value="msg91">MSG91</option>
            <option value="startmessaging">StartMessaging</option>
          </select>
        </div>

        <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
          <span className="material-symbols-outlined text-blue-500 text-[18px] mt-0.5">info</span>
          <p className="text-xs text-blue-700 font-medium">
            {smsProvider === 'msg91'
              ? 'Using MSG91 — configured via server environment variables.'
              : 'Using StartMessaging — API key configured via server environment variables.'}
          </p>
        </div>

        <div className="flex justify-end pt-2">
          <button
            onClick={() => {
              api.post('/api/admin/settings', { sms_provider: smsProvider })
                .then(() => showToast('SMS provider saved'))
                .catch(() => showToast('Failed to save', 'error'));
            }}
            className="px-6 py-2.5 rounded-full bg-primary text-white text-sm
                      font-bold hover:opacity-90 transition-all">
            Save SMS Settings
          </button>
        </div>
      </Section>

      {/* ── API Keys & Secrets ──────────────────────────────────────────── */}
      <Section icon="key" title="API Keys & Secrets">
        <p className="text-xs text-secondary -mt-2">
          Values you set here take effect immediately across the app, no redeploy needed.
          Values shown as "Env default" are still using the server's environment variable.
        </p>
        {secretsLoading ? (
          <div className="py-6 flex justify-center">
            <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-2">
            {Object.entries(
              secrets.reduce((acc, s) => {
                (acc[s.category] = acc[s.category] || []).push(s);
                return acc;
              }, {})
            ).map(([category, items]) => {
              const isOpen = openCategory === category;
              const setCount = items.filter(s => s.source === 'db').length;
              return (
                <div key={category} className="rounded-xl border border-outline-variant/20 overflow-hidden">
                  <button
                    onClick={() => setOpenCategory(isOpen ? null : category)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3
                               hover:bg-surface-container-low transition-all">
                    <span className="text-xs font-black uppercase tracking-widest text-secondary">
                      {category}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold text-outline">
                        {items.length} key{items.length !== 1 ? 's' : ''}
                        {setCount > 0 ? ` · ${setCount} admin-set` : ''}
                      </span>
                      <span className="material-symbols-outlined text-outline text-[18px]">
                        {isOpen ? 'expand_less' : 'expand_more'}
                      </span>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 space-y-2 border-t border-surface-container-high pt-3">
                      {items.map(s => (
                        <SecretRow key={s.key} secret={s} onSaved={loadSecrets} showToast={showToast} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* ── Danger Zone ──────────────────────────────────────────────── */}
      <Section icon="dangerous" title="Danger Zone">
        <div className="space-y-4">

          {[
            {
              label: 'Clear all inquiries',
              sub:   'Permanently delete all inquiry records from the database',
              btn:   'Clear Inquiries',
              color: 'bg-amber-100 text-amber-700 hover:bg-amber-600 hover:text-white',
            },
            {
              label: 'Delete all draft listings',
              sub:   'Remove all listings currently in draft / rejected state',
              btn:   'Delete Drafts',
              color: 'bg-amber-100 text-amber-700 hover:bg-amber-600 hover:text-white',
            },
            {
              label: 'Reset platform data',
              sub:   'Wipes all orders, inquiries, and listings. Cannot be undone.',
              btn:   'Reset Everything',
              color: 'bg-red-100 text-red-600 hover:bg-red-600 hover:text-white',
            },
          ].map((d) => (
            <div key={d.label}
              className="flex items-center justify-between gap-4 p-4
                         rounded-xl border border-red-100 bg-red-50/40">
              <div>
                <p className="text-sm font-semibold text-on-surface">{d.label}</p>
                <p className="text-xs text-secondary mt-0.5">{d.sub}</p>
              </div>
              <button
                onClick={() => {
                  if (window.confirm(`Are you sure? ${d.sub}`)) {
                    showToast(`${d.btn} — wire to API`, 'error');
                  }
                }}
                className={`px-4 py-2 rounded-full text-xs font-bold
                            flex-shrink-0 transition-all ${d.color}`}>
                {d.btn}
              </button>
            </div>
          ))}

        </div>
      </Section>

    </div>
  );
}